import { useEffect, useState } from 'react';

/** Звук у зрителя сейчас идёт в моно — запись стерео, а слышно одно и то же в обоих ушах.
 *
 *  Так бывает, когда микрофон блютус-наушников занят (созвон, открытые «Настройки → Звук»):
 *  macOS переводит их в режим гарнитуры — один канал и узкая полоса. Алексей 05.11
 *  (#4392–#4400) полчаса искал «моно на сайте», а виноват был выход Мака. Браузер честно
 *  говорит, сколько каналов у устройства вывода (destination.maxChannelCount): у гарнитуры
 *  это 1. Настройку «стерео как моно» в универсальном доступе так не увидеть — устройство
 *  остаётся стерео. */
let cached: boolean | null = null;
const subs = new Set<(v: boolean) => void>();

function measure(): void {
  try {
    const ctx = new AudioContext();
    const mono = ctx.destination.maxChannelCount > 0 && ctx.destination.maxChannelCount < 2;
    void ctx.close().catch(() => undefined);
    if (mono !== cached) {
      cached = mono;
      subs.forEach((f) => f(mono));
    }
  } catch {
    /* без Web Audio — молчим */
  }
}

let listening = false;
export function useMonoOutput(enabled: boolean): boolean {
  const [mono, setMono] = useState<boolean>(cached ?? false);
  useEffect(() => {
    if (!enabled) return;
    subs.add(setMono);
    if (cached == null) measure();
    else setMono(cached);
    // Наушники подключили/отключили, микрофон отпустили — пересчитать.
    if (!listening && navigator.mediaDevices?.addEventListener) {
      listening = true;
      navigator.mediaDevices.addEventListener('devicechange', () => window.setTimeout(measure, 300));
    }
    return () => { subs.delete(setMono); };
  }, [enabled]);
  return enabled && mono;
}

/** Каналы ogg/opus по заголовку OpusHead (байт 9 после метки); null — не opus. */
export async function oggChannels(blob: Blob): Promise<number | null> {
  const b = new Uint8Array(await blob.slice(0, 4096).arrayBuffer());
  const tag = [0x4f, 0x70, 0x75, 0x73, 0x48, 0x65, 0x61, 0x64]; // "OpusHead"
  for (let i = 0; i + 10 < b.length; i++) {
    if (tag.every((c, k) => b[i + k] === c)) return b[i + 9];
  }
  return null;
}
